import React, { useCallback, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Box, Tooltip } from '@mui/material';
import { IProposal } from '@shared/models/proposals.model';
import { useMouseEventCallback } from '@client/hooks/use-events.hooks';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import {
  closeMenu,
  menuMouseHandler,
  openMenu,
  useMenuPosition,
} from '@client/hooks/use-menu.hooks';
import { Menu } from '@ui/menus/Menu';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import {
  getSelectedProposalSearchValueFromUrl,
  resolveSelectedProposalIdForVersion,
  useSetSelectedProposalId,
} from '@client/hooks/use-selected-proposal-id.hooks';
import { getSelectedVersion } from '@client/store/ui';
import { useSelectedVersion } from '@client/store/ui/ui.hook';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import { AMP_PROPOSAL_DELETE } from '@shared/constants';
import {
  useCreateBaselineFromProposal,
  useDuplicateProposal,
} from '@features/proposals/hooks/use-proposals.hooks';
import { Icon } from '@ui/Icon';
import KebabButton from '@ui/menus/KebabMenu/components/KebabButton';
import { KebabMenuProps } from '@ui/menus/KebabMenu/kebabmenu.model';
import { mergeSx } from '@client/helpers/mui.helpers';
import { getSelectedProposalIdAfterDelete } from '@features/proposals/helpers/proposal-delete-selection.helpers';
import { getProposals } from '@shared/helpers/proposals';

const kebabMenuLayoutSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'opacity 0.3s',
} as const;

type IProposalKebabMenuInput = Pick<
  KebabMenuProps,
  | 'anchor'
  | 'onOpen'
  | 'hidden'
  | 'tooltip'
  | 'buttonProps'
  | 'className'
  | 'sx'
  | 'onClose'
> & {
  proposal: IProposal;
  /** Triggered when the user picks "Rename" – host opens the inline edit field. */
  onEdit?: () => void;
};

/**
 * Renders Rename / Duplicate / New version from proposal / Delete / Edit Baseline for an
 * {@link IProposal}. Delete dispatches {@link ProjectChangeType.ProposalDelete}
 * through {@link useApplyProjectChanges}, which also handles the confirm dialog
 * and Amplitude logging via its options.
 */
export const ProposalKebabMenu: React.FC<IProposalKebabMenuInput> = ({
  proposal,
  anchor,
  onOpen,
  hidden,
  tooltip,
  buttonProps,
  className,
  sx,
  onClose,
  onEdit,
}) => {
  const { id } = proposal;
  const menuPosition = useMenuPosition(id);
  const setSelectedProposalId = useSetSelectedProposalId();
  const selectedVersion = useSelectedVersion();
  const isLastProposal = (selectedVersion?.proposals?.length ?? 0) <= 1;
  const createBaselineFromProposal = useCreateBaselineFromProposal();
  const duplicateProposal = useDuplicateProposal();
  const undoRestoredSelectionRef = useRef(false);
  const [skipRestoreFocus, startSkipRestoreFocus, stopSkipRestoreFocus] =
    useBooleanState(false);

  const applyDelete = useApplyProjectChanges({
    amplitude: {
      eventType: AMP_PROPOSAL_DELETE,
      eventProperties: { ProposalId: id },
    },
    snackbar: {
      type: 'undo',
      message: 'Proposal deleted',
      onAfterUndo: () => {
        undoRestoredSelectionRef.current = true;
        setSelectedProposalId(id);
      },
    },
  });

  const isMenuOpen = Boolean(anchor || menuPosition);

  const handleMenuClose = useCallback(() => {
    closeMenu();
    onClose?.();
  }, [onClose]);

  const handleDuplicateClick = useCallback(async () => {
    handleMenuClose();
    await duplicateProposal(proposal);
  }, [duplicateProposal, proposal, handleMenuClose]);

  // Promote this proposal into a new standalone baseline version (proposal merged onto
  // the current baseline, no proposals on the result). The add flow navigates there.
  const handleNewVersionFromProposalClick = useCallback(async () => {
    // Menu.Item would close before onClick. closeMenu is Zustand (sync), so
    // commit skipRestoreFocus first or the close render restores kebab focus.
    flushSync(() => {
      startSkipRestoreFocus();
    });
    handleMenuClose();
    await createBaselineFromProposal(proposal);
  }, [
    createBaselineFromProposal,
    proposal,
    handleMenuClose,
    startSkipRestoreFocus,
  ]);

  const handleDeleteClick = useCallback(async () => {
    const version = getSelectedVersion(true);
    const proposals = getProposals(version);
    const selectedProposalId = resolveSelectedProposalIdForVersion(
      getSelectedProposalSearchValueFromUrl(),
      proposals.map(({ id }) => id),
    );
    const deletingSelectedProposal = selectedProposalId === id;
    if (deletingSelectedProposal) {
      setSelectedProposalId(
        getSelectedProposalIdAfterDelete(proposals, id, id),
      );
    }
    undoRestoredSelectionRef.current = false;

    handleMenuClose();

    const result = await applyDelete([
      {
        type: ProjectChangeType.ProposalDelete,
        versionId: version.id,
        proposalId: id,
      },
    ]);

    // Confirm dialog cancelled or flow aborted before persistence
    if (result.cancelled) {
      if (deletingSelectedProposal) setSelectedProposalId(id);
      return;
    }
    if (undoRestoredSelectionRef.current) return;
  }, [applyDelete, id, setSelectedProposalId, handleMenuClose]);

  const handleEditBaselineClick = useCallback(() => {
    handleMenuClose();
    setSelectedProposalId(undefined);
  }, [handleMenuClose, setSelectedProposalId]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleGlobalContextMenu = () => closeMenu();
    document.addEventListener('contextmenu', handleGlobalContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleGlobalContextMenu);
    };
  }, [isMenuOpen]);

  const handleButtonClick = useMouseEventCallback(
    (event) => {
      const anchorPosition = menuMouseHandler(event);
      if (anchorPosition) {
        stopSkipRestoreFocus();
        openMenu({ anchorPosition, id });
        onOpen?.(event.currentTarget);
      }
    },
    { stopPropagation: true },
  );

  return (
    <Box
      className={className}
      sx={mergeSx(kebabMenuLayoutSx, { opacity: hidden ? 0 : 1 }, sx)}
    >
      <Tooltip disableInteractive title={tooltip ?? 'Proposal options'}>
        <Box>
          <KebabButton
            {...buttonProps}
            disabled={hidden}
            hidden={hidden}
            aria-label={`${proposal.name} options`}
            onClick={handleButtonClick}
          />
        </Box>
      </Tooltip>

      <Menu
        open={isMenuOpen}
        anchorEl={anchor}
        anchorPosition={menuPosition}
        onClose={handleMenuClose}
        disableRestoreFocus={skipRestoreFocus}
      >
        <Menu.Item icon={<Icon category="mui" icon="Edit" />} onClick={onEdit}>
          Rename
        </Menu.Item>

        <Menu.Item
          icon={<Icon category="mui" icon="FileCopy" />}
          onClick={handleDuplicateClick}
        >
          Duplicate
        </Menu.Item>

        <Menu.Action
          icon={<Icon category="mui" icon="PostAdd" />}
          onClick={handleNewVersionFromProposalClick}
        >
          New version from proposal
        </Menu.Action>

        <Menu.Item
          icon={<Icon category="mui" icon="DeleteForever" />}
          onClick={handleDeleteClick}
          disabled={isLastProposal}
        >
          Delete
        </Menu.Item>

        <Menu.Item
          icon={<Icon category="mui" icon="EditOutlined" />}
          onClick={handleEditBaselineClick}
        >
          Edit Baseline
        </Menu.Item>
      </Menu>
    </Box>
  );
};
